import { useMemo } from "react";
import { useFullscreen } from "@/hooks/use-fullscreen";
import { Maximize, Minimize } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { DatePickerWithRange } from "@/components/ui/datetime-picker-with-range";
import { MultiSelect } from "@/components/ui/multi-select";
import { useBranch } from "@/context/branch-context";
import { useLocalization } from "@/context/localization-context";

export function DashboardControls() {
  const { dateRange, setDateRange } = useDashboardDateRange();
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const { t, tp } = useLocalization();
  const {
    branches,
    selectedBranchIds,
    branchUsageCounts,
    branchesLoading,
    canSelectBranches,
    selectBranches,
  } = useBranch();

  const branchOptions = useMemo(
    () =>
      [...(branches ?? [])]
        .sort((a, b) => {
          const selectedDelta =
            Number(selectedBranchIds.includes(b.id)) -
            Number(selectedBranchIds.includes(a.id));
          if (selectedDelta !== 0) return selectedDelta;

          const usageDelta =
            (branchUsageCounts[b.id] ?? 0) - (branchUsageCounts[a.id] ?? 0);
          if (usageDelta !== 0) return usageDelta;

          return a.name.localeCompare(b.name);
        })
        .map((branch) => ({
          label: branch.name,
          value: branch.id,
        })),
    [branchUsageCounts, branches, selectedBranchIds],
  );

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 text-muted-foreground">
      {canSelectBranches && (
        <MultiSelect
          options={branchOptions}
          selected={selectedBranchIds}
          onChange={selectBranches}
          placeholder={
            branchesLoading
              ? t("controls.loadingBranches")
              : t("controls.allBranches")
          }
          className="w-full min-w-0 text-foreground sm:w-auto sm:min-w-56 sm:max-w-80"
          maxDisplay={1}
          enableSearch
          searchPlaceholder={t("controls.searchBranch")}
          label={t("controls.branches")}
          clearAllLabel={t("controls.all")}
          noOptionsLabel={t("controls.noBranches")}
          noResultsLabel={t("controls.noBranchResults")}
          selectedCountLabel={(count) => tp("count.branches", count)}
        />
      )}
      <DatePickerWithRange
        value={dateRange}
        onChange={setDateRange}
        variant="default"
        className="text-sm text-foreground"
      />
      <Button
        onClick={toggleFullscreen}
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
        aria-label={
          isFullscreen ? t("common.exitFullscreen") : t("common.fullscreen")
        }
      >
        {isFullscreen ? (
          <Minimize className="h-4 w-4" />
        ) : (
          <Maximize className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}
